import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  IconButton,
  Typography
} from '@mui/material';
import { Add, Close } from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../../../../app/hooks';
import { toggleRuleModal, closeRuleModal } from '../../../../services/ContractService/contractSlice';
import { useGetRuleQuery } from '../../../../services/ContractService';
import { ApcCarveOuts } from './ruleTemplates/ApcCarveOuts';
import { PercentOfFeeSchedule } from './ruleTemplates/PercentOfFeeSchedule';
import { DrgConversions } from './ruleTemplates/DrgConversions';
import { StatusBasedCaseRate } from './ruleTemplates/StatusBasedCaseRate';
import { MedicarePercentByFeeType } from './ruleTemplates/MedicarePercentByFeeType';
import { RateBasedOnFeeSchedule } from './ruleTemplates/RateBasedOnFeeSchedule';
import { StatusBasedComprehensive } from './ruleTemplates/StatusBasedComprehensive';
import { PerDayAddOn } from './ruleTemplates/PerDayAddOn';
import { ZeroRateOverride } from './ruleTemplates/ZeroRateOverride';
import { CodingComboCaseRate } from './ruleTemplates/CodingComboCaseRate';

const RuleModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector((state) => state.contract.ruleModalOpen);
  const selectedRuleId = useAppSelector((state) => state.contract.selectedRuleId);
  
  const [ruleType, setRuleType] = useState('NotSelected');
  const [ruleDescription, setRuleDescription] = useState('');
  const [ruleString, setRuleString] = useState('');
  
  const { data: ruleData, isLoading } = useGetRuleQuery(
    { ruleId: selectedRuleId! },
    { skip: !selectedRuleId || selectedRuleId === '0' }
  );

  useEffect(() => {
    if (selectedRuleId === '0') {
      setRuleType('NotSelected');
      setRuleDescription('');
      setRuleString('');
    } else if (ruleData) {
      setRuleType(ruleData.ruleType);
      setRuleDescription(ruleData.description);
      setRuleString(ruleData.rule);
    }
  }, [selectedRuleId, ruleData]);

  const handleClose = () => {
    dispatch(closeRuleModal());
    setRuleType('NotSelected');
    setRuleDescription('');
    setRuleString('');
  };

  const ruleTypes = [
    { value: 'NotSelected', label: 'Select Rule Type' },
    { value: 'ApcCarveouts', label: 'APC Carveouts' },
    { value: 'PercentOfFeeSchedule', label: 'Percent of Fee Schedule' },
    { value: 'DrgConversion', label: 'DRG Conversion' },
    { value: 'StatusBasedCaseRate', label: 'Status Based Case Rate' },
    { value: 'MedicarePercentByFeeType', label: 'Medicare Percent by Fee Type' },
    { value: 'RateBasedOnFeeSchedule', label: 'Rate Based on Fee Schedule' },
    { value: 'StatusBasedComprehensive', label: 'Status Based Comprehensive' },
    { value: 'PerDayAddOn', label: 'Per Day Add On' },
    { value: 'ZeroRateOverride', label: 'Zero Rate Override' },
    { value: 'CodingComboCaseRate', label: 'Coding Combo Case Rate' },
  ];

  const renderRuleTemplate = () => {
    if (!selectedRuleId) return null;

    switch (ruleType) {
      case 'ApcCarveouts':
        return (
          <ApcCarveOuts
            ruleId={selectedRuleId}
            ruleString={ruleString}
            ruleDescription={ruleDescription}
          />
        );
      case 'PercentOfFeeSchedule':
        return (
          <PercentOfFeeSchedule
            ruleId={selectedRuleId}
            ruleString={ruleString}
            ruleDescription={ruleDescription}
          />
        );
      case 'DrgConversion':
        return (
          <DrgConversions
            ruleId={selectedRuleId}
            ruleString={ruleString}
            ruleDescription={ruleDescription}
          />
        );
      case 'StatusBasedCaseRate':
        return (
          <StatusBasedCaseRate
            ruleId={selectedRuleId}
            ruleString={ruleString}
            ruleDescription={ruleDescription}
          />
        );
      case 'MedicarePercentByFeeType':
        return (
          <MedicarePercentByFeeType
            ruleId={selectedRuleId}
            ruleString={ruleString}
            ruleDescription={ruleDescription}
          />
        );
      case 'RateBasedOnFeeSchedule':
        return (
          <RateBasedOnFeeSchedule
            ruleId={selectedRuleId}
            ruleString={ruleString}
            ruleDescription={ruleDescription}
          />
        );
      case 'StatusBasedComprehensive':
        return (
          <StatusBasedComprehensive
            ruleId={selectedRuleId}
            ruleString={ruleString}
            ruleDescription={ruleDescription}
          />
        );
      case 'PerDayAddOn':
        return (
          <PerDayAddOn
            ruleId={selectedRuleId}
            ruleString={ruleString}
            ruleDescription={ruleDescription}
          />
        );
      case 'ZeroRateOverride':
        return (
          <ZeroRateOverride
            ruleId={selectedRuleId}
            ruleString={ruleString}
            ruleDescription={ruleDescription}
          />
        );
      case 'CodingComboCaseRate':
        return (
          <CodingComboCaseRate
            ruleId={selectedRuleId}
            ruleString={ruleString}
            ruleDescription={ruleDescription}
          />
        );
      default:
        return (
          <Typography variant="body2" color="text.secondary">
            Please select a rule type to configure the rule.
          </Typography>
        );
    }
  };

  return (
    <>
      <Button
        startIcon={<Add />}
        variant="outlined"
        onClick={() => dispatch(toggleRuleModal('0'))}
      >
        Add Rule
      </Button>

      <Dialog open={isOpen} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {selectedRuleId === '0' ? 'Add New Rule' : 'Edit Rule'}
            </Typography>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {selectedRuleId === '0' && (
              <TextField
                select
                label="Rule Type"
                value={ruleType}
                onChange={(e) => setRuleType(e.target.value)}
                fullWidth
                required
              >
                {ruleTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {selectedRuleId !== '0' && ruleData && (
              <TextField
                label="Rule Type"
                value={ruleData.ruleType}
                disabled
                fullWidth
              />
            )}

            <TextField
              label="Description"
              value={ruleDescription}
              onChange={(e) => setRuleDescription(e.target.value)}
              fullWidth
            />

            {ruleType !== 'NotSelected' && renderRuleTemplate()}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export { RuleModal };